/**
 * Formats a time in 12-hour format with AM/PM
 * @param {Date|string|number|null} time - The time to format
 * @param {string} [venueTimeZone] - Optional venue timezone (e.g., 'America/Los_Angeles')
 * @returns {string} Formatted time string (e.g. "11:30AM") or empty string if time is null
 */
export const formatTimeAmPm = (time, venueTimeZone = null) => {
  if (!time) return '';
  
  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  
  // If venue timezone is provided, format in that timezone
  if (venueTimeZone) {
    options.timeZone = venueTimeZone;
  }
  
  return new Date(time).toLocaleTimeString('en-US', options);
};

/**
 * Formats a time in 12-hour format with spaced AM/PM
 * @param {Date|string|number|null} time - The time to format
 * @param {string} [venueTimeZone] - Optional venue timezone (e.g., 'America/Los_Angeles')
 * @returns {string} Formatted time string (e.g. "11:30 AM") or empty string if time is null
 */
export const formatTimeSpacedAmPm = (time, venueTimeZone = null) => {
  if (!time) return '';
  return formatTimeAmPm(time, venueTimeZone).replace('AM', ' AM').replace('PM', ' PM');
};

/**
 * Formats a time range with spaced AM/PM
 * @param {Date|string|number|null} startTime - The start time
 * @param {Date|string|number|null} endTime - The end time
 * @param {string} [venueTimeZone] - Optional venue timezone (e.g., 'America/Los_Angeles')
 * @returns {string} Formatted time range (e.g. "11:30 AM - 12:00 PM") or empty string if either time is null
 */
export const formatTimeRange = (startTime, endTime, venueTimeZone = null) => {
  if (!startTime || !endTime) return '';
  const start = formatTimeSpacedAmPm(startTime, venueTimeZone);
  const end = formatTimeSpacedAmPm(endTime, venueTimeZone);
  return `${start} - ${end}`;
};

/**
 * Formats a time for display in 12-hour format with AM/PM
 * @param {number|null} time - Unix timestamp in milliseconds
 * @param {string} [venueTimeZone] - Optional venue timezone (e.g., 'America/Los_Angeles')
 * @returns {string} Formatted time string (e.g. "11:30 AM") or empty string if time is null
 */
export const formatTimeForDisplay = (time, venueTimeZone = null) => {
  if (!time) return '';

  // Convert Unix timestamp to Date and format
  const date = time instanceof Date ? time : new Date(time);
  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  
  // If venue timezone is provided, format in that timezone
  if (venueTimeZone) {
    options.timeZone = venueTimeZone;
  }
  
  return date.toLocaleTimeString('en-US', options);
};

/**
 * Formats scheduled hours with special handling for zero minutes
 * @param {number} totalMinutes - Total number of minutes
 * @param {boolean} [includeScheduledText=true] - Whether to append "scheduled" to the output
 * @returns {string} Formatted string (e.g. "2 hours scheduled" or "2 hours 30 minutes scheduled")
 */
export const formatScheduledHours = (totalMinutes, includeScheduledText = true) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);

  let formattedTime;
  if (minutes === 0) {
    const hourSuffix = hours !== 1 ? 's' : '';
    formattedTime = `${hours} hour${hourSuffix}`;
  } else {
    formattedTime = formatMsToHoursAndMinutes(totalMinutes * 60 * 1000, false);
  }

  return includeScheduledText ? `${formattedTime} scheduled` : formattedTime;
};

/**
 * Formats a time difference into a human-readable string, handling both same-day and different-day cases
 * @param {Date|string} targetTime - The target time to compare against
 * @param {Date|string} [now=new Date()] - The reference time (defaults to current time)
 * @returns {string} Formatted time difference (e.g. "In 2 hours 30 minutes" or "3 days ago")
 */
export const formatTimeDifference = (scheduledTime) => {
  const now = new Date();
  const targetDate = new Date(scheduledTime);
  const currentDate = new Date(now);

  // Set both dates to start of day for consistent day comparison
  const targetDayStart = new Date(targetDate);
  const currentDayStart = new Date(currentDate);

  targetDayStart.setHours(0, 0, 0, 0);
  currentDayStart.setHours(0, 0, 0, 0);

  // If the target time is in a different day, return the number of days difference
  if (targetDayStart.getTime() !== currentDayStart.getTime()) {
    const days = Math.floor((targetDayStart - currentDayStart) / (1000 * 60 * 60 * 24));
    const daySuffix = Math.abs(days) > 1 ? 's' : '';
    const daysText = `${Math.abs(days)} day${daySuffix}`;

    return days > 0 ? `In ${daysText}` : `${daysText} ago`;
  }

  // If the target time is in the same day, return the number of hours and minutes difference
  const diffInMs = targetDate - currentDate;
  const isPast = diffInMs < 0;
  const formattedTime = formatMsToHoursAndMinutes(Math.abs(diffInMs));

  return isPast ? `${formattedTime} ago` : `In ${formattedTime}`;
};

/**
 * Calculates and formats the duration between two timestamps
 * @param {number|string|Date} start - Start timestamp
 * @param {number|string|Date} end - End timestamp
 * @returns {string} Formatted duration (e.g. "2 hr 30 min" or "45 min")
 */
export const formatDurationBetweenTimestamps = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffInMs = endDate - startDate;
  return formatMsToHoursAndMinutes(diffInMs, true);
};

/**
 * Formats a duration in milliseconds into a human-readable string with hours and minutes
 * @param {number} milliseconds - Duration in milliseconds
 * @param {boolean} shortFormat - If true, uses short format (e.g. "2 hr 30 min" instead of "2 hours 30 minutes")
 * @param {boolean} includeAnd - If true, includes 'and' between hours and minutes (e.g. "2 hours and 30 minutes")
 * @returns {string} Formatted duration (e.g. "2 hours 30 minutes" or "2 hr 30 min" or "2 hours and 30 minutes")
 */
export const formatMsToHoursAndMinutes = (milliseconds, shortFormat = false, includeAnd = false) => {
  const minutes = Math.floor(Math.abs(milliseconds) / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (shortFormat) {
    const hoursPart = hours > 0 ? `${hours}hr ` : '';
    return `${hoursPart}${remainingMinutes}min`;
  }

  let hoursPart = '';
  if (hours > 0) {
    const hourSuffix = hours > 1 ? 's' : '';
    hoursPart = `${hours} hour${hourSuffix}`;
  }

  const minuteSuffix = remainingMinutes > 1 ? 's' : '';
  const minutesPart = `${remainingMinutes} minute${minuteSuffix}`;

  if (!hoursPart) return minutesPart;
  if (!remainingMinutes) return hoursPart;

  return includeAnd ? `${hoursPart} and ${minutesPart}` : `${hoursPart} ${minutesPart}`;
};

/**
 * Formats a duration in milliseconds into a concise string with hours and minutes
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration string (e.g., "2h 30m" or "45m")
 */
export const formatDuration = (milliseconds) => {
  return formatMsToHoursAndMinutes(milliseconds, true).replace('hr', 'h').replace('min', 'm');
};

